import { api } from '@tg/app-core';
import type { QueryClient } from '@tanstack/react-query';
import { TRPCClientError } from '@trpc/client';
import { useSyncExternalStore } from 'react';
import { dayKey, patchCaches, whoKey, type DayOp } from './days';
import { idbAll, idbDel, idbGet, idbSet } from './idb';
import { isOnline, onNet, reportNetFail, reportNetOk } from './net';

/* Очередь правок дня (outbox), как в оффлайне Google Таблиц.
   Сеть есть и очередь пуста — запрос уходит сразу, как раньше (run → api). Иначе правка встаёт
   в очередь на устройстве и сразу видна на экране; когда связь появится, очередь уходит по порядку,
   по одной операции: с id (повтор не применится дважды) и версией дня, от которой сделана правка.
   День правили на другом устройстве — очередь встаёт, тренер выбирает «моя версия / с сервера». */

export interface Op {
  id: string;
  seq: number;
  /** День (кто@дата) или все дни человека/группы (кто@*) */
  key: string;
  at: number;
  /** «Моя версия» после конфликта — без проверки версии */
  force?: boolean;
  o: DayOp;
}

export interface OutboxState {
  pending: number;
  sending: boolean;
  conflict: Op | null;
  /** Правки, которые сервер не принял (клиент удалён, лимит…) */
  errors: string[];
  /** Очередь только что ушла целиком — «Все изменения сохранены» */
  saved: boolean;
}

let qc: QueryClient | null = null;
let ops: Op[] = [];
let seq = 0;
/** Последняя известная версия дня (кто@дата → rev): с ней уходит правка из очереди */
let revs: Record<string, number> = {};
let state: OutboxState = { pending: 0, sending: false, conflict: null, errors: [], saved: false };
const subs = new Set<() => void>();
const reloadSubs = new Set<(key: string) => void>();
let savedTimer: ReturnType<typeof setTimeout> | undefined;

function emit(p: Partial<OutboxState> = {}) {
  state = { ...state, ...p, pending: ops.length };
  subs.forEach((f) => f());
}

const keyOf = (o: DayOp) => (o.op === 'saveDays' ? whoKey(o.input.who) + '@*' : dayKey(o.input.who, o.input.date));
const uuid = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 3) | 8).toString(16);
      });

const saveRevs = () => idbSet('meta', 'revs', revs);
const putOp = (o: Op) => idbSet('outbox', o.seq, o);

/** Аккаунт открыт: очередь и версии дней с устройства, затем отправка */
export async function initOutbox(client: QueryClient) {
  qc = client;
  ops = ((await idbAll<Op>('outbox')) || []).sort((a, b) => a.seq - b.seq);
  seq = ops.length ? ops[ops.length - 1].seq : 0;
  revs = (await idbGet<Record<string, number>>('meta', 'revs')) || {};
  emit({ conflict: null, errors: [], saved: false, sending: false });
  kick();
}

/** Выход из аккаунта */
export function resetOutbox() {
  ops = [];
  revs = {};
  qc = null;
  emit({ conflict: null, errors: [], saved: false, sending: false });
}

export const pendingOps = () => ops.map((o) => o.o);

/** Конструктор открыл день: версия, от которой тренер правит (пока у дня нет правок в очереди) */
export function seenRev(key: string, rev: number | undefined) {
  if (rev == null || ops.some((o) => o.key === key)) return;
  if (revs[key] === rev) return;
  revs[key] = rev;
  saveRevs();
}

/** Ответ сервера со списком дней (trainer.days, offlinePack): это версии, которые тренер сейчас видит.
    Обновляем запомненную версию у дней без неотправленных правок — иначе удаление, копирование из календаря
    и другие действия, меняющие день, давали ложный конфликт при следующей правке без сети */
export function serverRows(rows: { key: string; date: string; rev: number }[], range?: { who: string; from: string; to: string }) {
  const pend = new Set(ops.map((o) => o.key));
  let changed = false;
  const seen = new Set<string>();
  for (const r of rows) {
    seen.add(r.key);
    if (pend.has(r.key) || revs[r.key] === r.rev) continue;
    revs[r.key] = r.rev;
    changed = true;
  }
  /* Дня в ответе нет — на сервере его удалили: версия 0 */
  if (range)
    for (const k of Object.keys(revs)) {
      const [w, d] = k.split('@');
      if (w === range.who && d >= range.from && d <= range.to && !seen.has(k) && !pend.has(k) && revs[k] !== 0) {
        revs[k] = 0;
        changed = true;
      }
    }
  if (changed) saveRevs();
}

/** Ответ сервера: новые версии дней */
function record(o: DayOp, r: unknown) {
  const x = (r || {}) as { rev?: number; revs?: Record<string, number> };
  if (o.op === 'saveDays') for (const [d, v] of Object.entries(x.revs || {})) revs[dayKey(o.input.who, d)] = v;
  else if (typeof x.rev === 'number') revs[dayKey(o.input.who, o.input.date)] = x.rev;
  saveRevs();
}

const isNetErr = (e: unknown) => e instanceof TRPCClientError && !e.data && !(e.shape as unknown);
const codeOf = (e: unknown) => (e instanceof TRPCClientError ? (e.data?.code as string | undefined) : undefined);

/** Ответ вместо сервера, пока правка в очереди */
function optimistic(o: DayOp): unknown {
  if (o.op === 'editDay') return { ok: true };
  if (o.op === 'saveDays') return { dates: [], revs: {} };
  return { status: o.input.published ? 'published' : 'draft' };
}

function call(o: DayOp, extra: Record<string, unknown> = {}) {
  const t = api.trainer as unknown as Record<string, { mutate: (i: unknown) => Promise<unknown> }>;
  return t[o.op].mutate({ ...o.input, ...extra });
}

function enqueue(o: DayOp) {
  const key = keyOf(o);
  const last = ops[ops.length - 1];
  /* Подряд идущие правки одного дня — одной операцией с последним содержимым (не ту, что уже уходит) */
  if (o.op === 'editDay' && last && last.key === key && last.o.op === 'editDay' && !(state.sending && last === ops[0])) {
    last.o = o;
    last.at = Date.now();
    putOp(last);
  } else {
    const op: Op = { id: uuid(), seq: ++seq, key, at: Date.now(), o };
    ops.push(op);
    putOp(op);
  }
  if (qc) patchCaches(qc, o);
  clearTimeout(savedTimer);
  emit({ saved: false });
}

/** Операция с днём: сразу на сервер, если можно, иначе — в очередь */
export async function run<T = unknown>(o: DayOp): Promise<T> {
  if (isOnline() && !ops.length && !state.conflict) {
    try {
      const r = await call(o);
      reportNetOk();
      record(o, r);
      return r as T;
    } catch (e) {
      if (!isNetErr(e)) throw e;
      reportNetFail();
    }
  }
  enqueue(o);
  return optimistic(o) as T;
}

let sending: Promise<void> | null = null;

/** Отправить очередь по порядку; уже отправляется — дождаться той же отправки */
export function kick(): Promise<void> {
  if (sending) return sending;
  if (!isOnline() || !ops.length || state.conflict || !qc) return Promise.resolve();
  sending = drain().finally(() => (sending = null));
  return sending;
}

async function drain() {
  emit({ sending: true, saved: false });
  let sent = 0;
  while (ops.length && isOnline() && !state.conflict) {
    const op = ops[0];
    const base = op.o.op === 'saveDays' || op.force ? undefined : revs[op.key];
    try {
      const r = await call(op.o, { opId: op.id, ...(base != null ? { baseRev: base } : {}) });
      reportNetOk();
      record(op.o, r);
      done(op);
      sent++;
    } catch (e) {
      if (isNetErr(e)) {
        reportNetFail();
        break;
      }
      const code = codeOf(e);
      if (code === 'CONFLICT') {
        emit({ conflict: op });
        break;
      }
      /* Сессия кончилась — очередь ждёт входа */
      if (code === 'UNAUTHORIZED') break;
      done(op);
      sent++;
      emit({ errors: [...state.errors, (e as Error).message || 'Изменение не сохранилось'] });
    }
  }
  const drained = !ops.length && sent > 0;
  emit({ sending: false, saved: drained });
  if (drained) {
    /* Всё ушло — свежие данные с сервера */
    qc?.invalidateQueries({ queryKey: [['trainer']] });
    clearTimeout(savedTimer);
    savedTimer = setTimeout(() => emit({ saved: false }), 3000);
  }
}

function done(op: Op) {
  ops = ops.filter((x) => x !== op);
  idbDel('outbox', op.seq);
  emit();
}

/** Конфликт: «моя версия» — правка уходит поверх серверной */
export function keepMine() {
  const c = state.conflict;
  if (!c) return;
  c.force = true;
  putOp(c);
  emit({ conflict: null });
  kick();
}

/** Конфликт: «с сервера» — правки этого дня из очереди отбрасываются, день перечитывается */
export function takeServer() {
  const c = state.conflict;
  if (!c) return;
  for (const o of ops.filter((x) => x.key === c.key)) idbDel('outbox', o.seq);
  ops = ops.filter((x) => x.key !== c.key);
  delete revs[c.key];
  saveRevs();
  emit({ conflict: null });
  qc?.invalidateQueries({ queryKey: [['trainer']] });
  reloadSubs.forEach((f) => f(c.key));
  kick();
}

export const dismissErrors = () => emit({ errors: [] });

/** Конструктор: день взят с сервера после конфликта — перечитать документ */
export function onReload(f: (key: string) => void) {
  reloadSubs.add(f);
  return () => {
    reloadSubs.delete(f);
  };
}

function onOutbox(f: () => void) {
  subs.add(f);
  return () => {
    subs.delete(f);
  };
}
export const useOutbox = () => useSyncExternalStore(onOutbox, () => state);

/* Связь вернулась — очередь уходит */
onNet(() => {
  if (isOnline()) kick();
});
